// Receiving against a purchase order, a delivery at a time (no Firebase, no DOM; unit-tested).
//
// A PO stays open until everything on it has come in, or someone closes it because the rest isn't
// coming. Each delivery is one entry in po.receipts: { id: { date, who, doc, slipUrl, lines: { l0: qty } } }
// (keyed l0, l1 by line position: plain numbers would make the database hand back an array).
// line.received is always the running total. POs closed before deliveries were tracked have no
// receipts, and their line.received is what came in. An open PO with no receipts has received
// nothing, whatever line.received says (Reorder and purchase lists used to pre-fill it with the
// ordered quantity).

export const UNASSIGNED = 'Unassigned';

const lineKey = i => `l${i}`;
const whole = n => Math.max(0, Math.round(Number(n) || 0));

export const poLines = po => (Array.isArray(po?.lines) ? po.lines : Object.values(po?.lines || {}));
export const poReceipts = po => Object.entries(po?.receipts || {}).map(([id, r]) => ({ id, ...r }));

/** How much of line `i` has come in so far. */
export function lineReceived(po, i) {
  const receipts = poReceipts(po);
  if (receipts.length) return receipts.reduce((t, r) => t + whole(r.lines?.[lineKey(i)]), 0);
  return po?.open ? 0 : whole(poLines(po)[i]?.received);
}

/** How much of line `i` is still to come (0 when it came in full or over). */
export function lineDue(po, i) {
  return Math.max(0, whole(poLines(po)[i]?.ordered) - lineReceived(po, i));
}

/** The PO at a glance. state: waiting | partial | received | short (closed with some never received). */
export function poStatus(po) {
  const lines = poLines(po);
  const ordered = lines.reduce((t, l) => t + whole(l?.ordered), 0);
  const received = lines.reduce((t, _, i) => t + lineReceived(po, i), 0);
  const due = lines.reduce((t, _, i) => t + lineDue(po, i), 0);
  const state = po?.open ? (received ? 'partial' : 'waiting') : (due ? 'short' : 'received');
  return { state, ordered, received, due };
}

/** counts ({ lineIndex: qty }) with blanks and zeros taken out and quantities made whole numbers. */
export function cleanCounts(counts) {
  const out = {};
  Object.entries(counts || {}).forEach(([i, n]) => { if (whole(n)) out[i] = whole(n); });
  return out;
}

/** What a delivery leaves behind: still due across the PO, and whether that finishes it. */
export function afterDelivery(po, counts) {
  const c = cleanCounts(counts);
  const due = poLines(po).reduce((t, l, i) => t + Math.max(0, whole(l?.ordered) - lineReceived(po, i) - (c[i] || 0)), 0);
  return { units: Object.values(c).reduce((t, n) => t + n, 0), stillDue: due, closes: due === 0 };
}

/**
 * Database fields to change for one delivery, relative to the PO: the receipt, each line's new
 * total, and open/closedDate once nothing is left. { fields, closes }; fields is empty when the
 * counts are all zero.
 */
export function deliveryFields(po, counts, { id, date, at, who, doc, slipUrl }) {
  const c = cleanCounts(counts);
  if (!Object.keys(c).length) return { fields: {}, closes: false };
  const lines = {};
  const fields = {};
  Object.entries(c).forEach(([i, n]) => {
    lines[lineKey(i)] = n;
    fields[`lines/${i}/received`] = lineReceived(po, Number(i)) + n;
  });
  const receipt = { date, at, who: who || '', lines };
  if (doc) receipt.doc = doc;
  if (slipUrl) receipt.slipUrl = slipUrl;
  fields[`receipts/${id}`] = receipt;
  const { closes } = afterDelivery(po, c);
  if (closes) { fields.open = false; fields.closedDate = date; }
  return { fields, closes };
}

/** Fields that close a PO because nothing more is coming; what never came stays visible on it. */
export function closeFields({ date, who }) {
  return { open: false, closedDate: date, closedShortBy: who || '' };
}

/**
 * Fields that reopen a closed PO. One closed before deliveries were tracked first gets its one
 * delivery written as a receipt, so what already came in isn't counted as due.
 */
export function reopenFields(po, { id, at }) {
  const fields = { open: true, closedDate: null, closedShortBy: null };
  if (!poReceipts(po).length) {
    const lines = {};
    poLines(po).forEach((l, i) => { if (whole(l?.received)) lines[lineKey(i)] = whole(l.received); });
    if (Object.keys(lines).length) fields[`receipts/${id}`] = { date: po.closedDate || '', at, who: '', lines };
  }
  return fields;
}

/**
 * A copy of the PO with database fields applied (paths as above; null removes), so this device
 * shows what was just written. Nothing in the original is changed.
 */
export function withFields(po, fields) {
  const next = { ...po, lines: poLines(po).map(l => ({ ...l })), receipts: { ...(po.receipts || {}) } };
  Object.entries(fields).forEach(([path, value]) => {
    const [a, b, c] = path.split('/');
    if (a === 'lines') next.lines[Number(b)][c] = value;
    else if (a === 'receipts') next.receipts[b] = value;
    else if (value === null) delete next[a];
    else next[a] = value;
  });
  if (!Object.keys(next.receipts).length) delete next.receipts;
  return next;
}

/**
 * The bin a received part goes into. "Unassigned" on a PO line only means no bin had been named
 * when the PO was made, so a part that now has a real bin goes there.
 */
export function receiveBin(bins, lineBin) {
  const named = String(lineBin || '').trim();
  if (named && named !== UNASSIGNED) return named;
  return (bins || []).find(b => b.name && b.name !== UNASSIGNED)?.name || UNASSIGNED;
}

/** The part's bins after qty arrives in binName. A new bin replaces an empty "Unassigned" one. */
export function addToBins(bins, binName, qty) {
  const list = (bins || []).map(b => ({ ...b }));
  const hit = list.find(b => b.name === binName);
  if (hit) { hit.qty = whole(hit.qty) + whole(qty); return list; }
  const kept = list.filter(b => b.name !== UNASSIGNED || whole(b.qty));
  return [...kept, { name: binName, qty: whole(qty) }];
}
